import React from 'react';
import { Link, useFetcher } from 'react-router-dom';
import {
  ArchiveBoxIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  PlusIcon,
  SwatchIcon
} from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';
import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { Action, Resource, Visibility, type AgencyDesignFieldsFragment } from '~/api/graphql';
import { PermissionButton, usePermissions } from '~/components/permissions';
import { RowActionsMenu, type RowAction } from '~/components/row-actions-menu';
import { VisibilityBadge } from '~/components/visibility/visibility-badge';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { PALETTE_ROLES } from './design-brand';
import { DESIGN_NEW_PATH, DESIGN_PATH, designEditPath } from './design-paths';

type Design = AgencyDesignFieldsFragment;

type ListAction = 'duplicate' | 'archive' | 'default';

const IN_FLIGHT: Record<ListAction, string> = {
  duplicate: 'Duplicating…',
  archive: 'Archiving…',
  default: 'Making default…'
};

const CREATE_DESIGN = [{ resource: Resource.HtmlDocumentTemplate, action: Action.Create }];

/** The server keeps the default design visible to everyone, so only such a design can become it. */
const isPublic = (design: Design) => design.visibility.level === Visibility.Public;
const VISIBLE_FIRST = 'Visible to everyone first';

/** No design becomes the default by itself, so an agency left with one is asked. */
export const designOfferedAsDefault = (
  designs: Design[],
  defaultDesignId: string | null
): Design | null => (!defaultDesignId && designs.length === 1 ? designs[0] : null);

const NewDesignButton = () => (
  <PermissionButton
    permissions={CREATE_DESIGN}
    whenDenied="hide"
    color="sky"
    href={DESIGN_NEW_PATH}
  >
    <PlusIcon />
    New design
  </PermissionButton>
);

const typefacesOf = ({ brand }: Design) =>
  brand.headingTypeface === brand.bodyTypeface
    ? brand.bodyTypeface
    : `${brand.headingTypeface} headings, ${brand.bodyTypeface} body`;

const failureOf = (result: ActionResult<unknown>): string | null =>
  result?.applicationError?.message ?? result?.validationErrors?.[0]?.message ?? null;

export const DesignList = ({
  designs,
  defaultDesignId
}: {
  designs: Design[];
  defaultDesignId: string | null;
}) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(Resource.HtmlDocumentTemplate, Action.Create);
  const canUpdate = hasPermission(Resource.HtmlDocumentTemplate, Action.Update);
  const isAdmin = hasPermission(Resource.Agency, Action.Update);

  const fetcher = useFetcher<ActionResult<unknown>>();
  const busy = fetcher.state !== 'idle';
  const [submitted, setSubmitted] = React.useState<{ designId: string; action: ListAction }>();
  const inFlight = busy ? submitted : undefined;
  const run = (design: Design, action: ListAction) => {
    setSubmitted({ designId: design.id, action });
    fetcher.submit(
      {},
      {
        method: 'post',
        encType: 'application/json',
        action: `${DESIGN_PATH}/${design.id}/${action}`
      }
    );
  };
  const error = busy ? null : failureOf(fetcher.data);
  const [archiving, setArchiving] = React.useState<Design | null>(null);
  const offered = isAdmin ? designOfferedAsDefault(designs, defaultDesignId) : null;

  const actionsFor = (design: Design): RowAction[] => [
    ...(canCreate
      ? [
          {
            label: 'Duplicate',
            icon: DocumentDuplicateIcon,
            onSelect: () => run(design, 'duplicate')
          }
        ]
      : []),
    ...(isAdmin && !design.isDefault
      ? [
          isPublic(design)
            ? {
                label: 'Make default',
                icon: CheckBadgeIcon,
                onSelect: () => run(design, 'default')
              }
            : {
                label: 'Make default',
                icon: CheckBadgeIcon,
                disabled: true,
                description: VISIBLE_FIRST,
                onSelect: () => {}
              }
        ]
      : []),
    // The server refuses to archive the default: another design has to take its place first.
    ...(canUpdate
      ? [
          design.isDefault
            ? {
                label: 'Archive',
                icon: ArchiveBoxIcon,
                disabled: true,
                description: 'Make another design default first',
                onSelect: () => {}
              }
            : { label: 'Archive', icon: ArchiveBoxIcon, onSelect: () => setArchiving(design) }
        ]
      : [])
  ];

  return (
    <>
      <div className="divide-y divide-neutral-200 rounded-lg bg-white shadow dark:divide-neutral-700 dark:bg-neutral-800">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
                Designs
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                {defaultDesignId
                  ? 'New documents use the default design.'
                  : 'New documents start with no design.'}{' '}
                Change a document's design from its Design menu.
              </p>
            </div>
            {designs.length > 0 && <NewDesignButton />}
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>

        {offered && (
          <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">
            <p className="text-sm text-neutral-700 dark:text-neutral-300">
              Make {offered.name} your default design?
            </p>
            <Button
              color="sky"
              disabled={busy || !isPublic(offered)}
              title={isPublic(offered) ? undefined : VISIBLE_FIRST}
              loading={inFlight?.designId === offered.id && inFlight.action === 'default'}
              onClick={() => run(offered, 'default')}
            >
              Make default
            </Button>
          </div>
        )}

        {designs.length === 0 ? (
          <div className="px-4 py-12 text-center sm:px-6">
            <SwatchIcon className="mx-auto size-12 text-neutral-400 dark:text-neutral-500" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              No designs
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {canCreate
                ? 'Add one to brand your documents.'
                : 'Ask someone who manages designs to add one.'}
            </p>
            <div className="mt-4">
              <NewDesignButton />
            </div>
          </div>
        ) : (
          <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {designs.map((design) => {
              const actions = actionsFor(design);
              const summary = (
                <>
                  <div className="hidden shrink-0 items-center gap-1 sm:flex">
                    {PALETTE_ROLES.map((role) => (
                      <span
                        key={role.key}
                        className="size-5 rounded-full ring-1 ring-black/10 dark:ring-white/25"
                        style={{ background: design.brand.palette[role.key] }}
                        title={`${role.label}: ${design.brand.palette[role.key]}`}
                      />
                    ))}
                  </div>
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm/6 font-semibold text-neutral-900 dark:text-white">
                      {design.name}
                      <VisibilityBadge visibility={design.visibility} variant="list" />
                      {design.isDefault && <Badge color="sky">Default</Badge>}
                    </p>
                    {inFlight?.designId === design.id ? (
                      <p
                        role="status"
                        className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400"
                      >
                        <Spinner size="size-4" color="neutral" />
                        {IN_FLIGHT[inFlight.action]}
                      </p>
                    ) : (
                      <p className="flex flex-wrap gap-x-2 text-sm text-neutral-500 dark:text-neutral-400">
                        <span>{typefacesOf(design)}</span>
                        {design.convertedFromStyle && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>Converted from Style {design.convertedFromStyle.title}</span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </>
              );
              return (
                <li
                  key={design.id}
                  aria-label={design.name}
                  className="flex items-center justify-between gap-4 px-4 py-4 sm:px-6"
                >
                  {canUpdate ? (
                    <Link
                      to={designEditPath(design.id)}
                      className="flex min-w-0 flex-1 items-center gap-4"
                    >
                      {summary}
                      <ChevronRightIcon
                        aria-hidden="true"
                        className="ml-auto size-5 flex-none text-neutral-400 dark:text-neutral-500"
                      />
                    </Link>
                  ) : (
                    <div className="flex min-w-0 flex-1 items-center gap-4">{summary}</div>
                  )}
                  {actions.length > 0 && (
                    <RowActionsMenu
                      label={`More actions for ${design.name}`}
                      actions={actions}
                      disabled={busy}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ArchiveConfirmDialog
        open={!!archiving}
        count={1}
        itemNoun="design"
        title={`Archive ${archiving?.name ?? 'design'}?`}
        description="Documents that use it keep their look, but no document can switch to it."
        onCancel={() => setArchiving(null)}
        onConfirm={() => {
          if (archiving) run(archiving, 'archive');
          setArchiving(null);
        }}
      />
    </>
  );
};
